import React, { useRef, useState, useEffect } from 'react';
import { styled } from '@ff/ui';
import Box from '@ff/ui/Box';
import Text from '@ff/ui/Text';
import useIntersectionObserver from 'shared/hooks/useIntersectionObserver';
import type { Props } from './props-schema';

export default function C10CenterMessage(props: Props) {
  const {
    background,
    subtitle,
    title,
    description,
    animation,
  } = props;

  const containerRef = useRef(null);
  const isVisible = useIntersectionObserver(containerRef, '0px', 0.1);
  const [hasTriggered, setHasTriggered] = useState(false);

  useEffect(() => {
    if (isVisible && !hasTriggered) {
      setHasTriggered(true);
    }
  }, [isVisible, hasTriggered]);

  const {
    enabled = false,
    type = 'SLIDE',
    direction = 'UP',
    duration: rawDuration = 0.8,
    initialDelay: rawInitialDelay = 0,
    stagger: rawStagger = 0.2,
    animateHeader = true,
    animateBlocks = true,
  } = animation || {};

  const duration = typeof rawDuration === 'string' ? parseFloat(rawDuration) : rawDuration;
  const initialDelay = typeof rawInitialDelay === 'string' ? parseFloat(rawInitialDelay) : rawInitialDelay;
  const stagger = typeof rawStagger === 'string' ? parseFloat(rawStagger) : rawStagger;

  const useBackgroundColor = background?.useBackgroundColor || false;
  const backgroundColor = background?.backgroundColor || '#000000';
  const desktopImage = background?.desktopImage;
  const tabletImage = background?.tabletImage;
  const mobileImage = background?.mobileImage;

  return (
    <Container
      ref={containerRef}
      useBackgroundColor={useBackgroundColor}
      backgroundColor={backgroundColor}
      desktopImage={desktopImage}
      tabletImage={tabletImage}
      mobileImage={mobileImage}

    >
      <ContentContainer>
        {subtitle?.text && (
          <AnimatedBox
            isVisible={enabled ? hasTriggered : true}
            type={type}
            direction={direction}
            duration={duration}
            delay={initialDelay}
            enabled={enabled && animateHeader}
          >
            <Subtitle
              subtitleColor={subtitle?.color || '#827667'}
              gradient={subtitle?.gradient}
            >
              {subtitle.text}
            </Subtitle>
          </AnimatedBox>
        )}
        {title?.text && (
          <AnimatedBox
            isVisible={enabled ? hasTriggered : true}
            type={type}
            direction={direction}
            duration={duration}
            delay={initialDelay + 0.1}
            enabled={enabled && animateHeader}
          >
            <Title
              titleColor={title?.color || '#FFD9A5'}
              gradient={title?.gradient}
            >
              {title.text}
            </Title>
          </AnimatedBox>
        )}
        {description?.text && (
          <AnimatedBox
            isVisible={enabled ? hasTriggered : true}
            type={type}
            direction={direction}
            duration={duration}
            delay={initialDelay + (animateHeader ? 0.2 : 0)}
            enabled={enabled && animateBlocks}
          >
            <Description descriptionColor={description?.color || '#8B8B8B'}>
              {description.text}
            </Description>
          </AnimatedBox>
        )}
      </ContentContainer>
    </Container >
  );
}

const AnimatedBox = styled(Box)<{
  isVisible: boolean;
  type: string;
  direction: string;
  duration: number;
  delay: number;
  enabled: boolean;
}>(({ isVisible, type, direction, duration, delay, enabled }) => {
  let transform = 'none';
  const opacity = isVisible ? 1 : 0;

  if (enabled && !isVisible) {
    if (type === 'SLIDE') {
      switch (direction) {
        case 'UP': transform = 'translateY(20px)'; break;
        case 'DOWN': transform = 'translateY(-20px)'; break;
        case 'LEFT': transform = 'translateX(20px)'; break;
        case 'RIGHT': transform = 'translateX(-20px)'; break;
        default: transform = 'translateY(20px)';
      }
    } else if (type === 'SCALE') {
      transform = 'scale(0.95)';
    }
  }

  return {
    opacity: enabled ? opacity : 1,
    transform: enabled ? (isVisible ? 'none' : transform) : 'none',
    transition: enabled ? `opacity ${duration}s ease-out ${delay}s, transform ${duration}s ease-out ${delay}s` : 'none',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  };
});

const Container = styled(Box)<{
  useBackgroundColor: boolean;
  backgroundColor: string;
  desktopImage?: string;
  tabletImage?: string;
  mobileImage?: string;
}>(({ theme, useBackgroundColor, backgroundColor, desktopImage, tabletImage, mobileImage }) => ({
  position: 'relative',
  width: '100%',
  minHeight: '100vh',
  backgroundColor: useBackgroundColor ? backgroundColor : '#000000',
  backgroundSize: 'cover',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'center center',
  // Only show background images if useBackgroundColor is false
  // Mobile: use mobile image if available, otherwise tablet, otherwise desktop
  backgroundImage: !useBackgroundColor && (mobileImage ? `url(${mobileImage})` : (tabletImage ? `url(${tabletImage})` : (desktopImage ? `url(${desktopImage})` : undefined))),
  padding: '30px 15px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  [theme.mediaQueries.sm]: {
    backgroundImage: !useBackgroundColor && (tabletImage ? `url(${tabletImage})` : (desktopImage ? `url(${desktopImage})` : undefined)),
    padding: '40px 20px',
  },
  [theme.mediaQueries.md]: {
    backgroundImage: !useBackgroundColor && (desktopImage ? `url(${desktopImage})` : undefined),
    padding: '60px 40px',
  },
}));

const ContentContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: '754px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '20px',
  textAlign: 'center',
  [theme.mediaQueries.sm]: {
    gap: '24px',
  },
  [theme.mediaQueries.md]: {
    gap: '30px',
  },
}));

const Subtitle = styled(Text)<{
  subtitleColor?: string;
  gradient?: string;
}>(({ theme, subtitleColor, gradient }) => ({
  fontSize: '24px',
  lineHeight: 1.3,
  fontWeight: 400,
  width: '100%',
  ...(gradient && gradient.trim() ? {
    background: gradient,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  } : {
    color: subtitleColor || '#827667',
  }),
  [theme.mediaQueries.sm]: {
    fontSize: '28px',
  },
  [theme.mediaQueries.md]: {
    fontSize: '30px',
  },
}));

const Title = styled(Text)<{
  titleColor?: string;
  gradient?: string;
}>(({ theme, titleColor, gradient }) => ({
  fontSize: '28px',
  lineHeight: 1.3,
  fontWeight: 400,
  paddingBottom: '52px',
  paddingLeft: '25px',
  paddingRight: '25px',
  width: '100%',
  whiteSpace: 'pre-line',
  ...(gradient && gradient.trim() ? {
    background: gradient,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  } : {
    color: titleColor || '#FFD9A5',
  }),
  [theme.mediaQueries.sm]: {
    fontSize: '32px',
  },
  [theme.mediaQueries.md]: {
    fontSize: '36px',
  },
}));

const Description = styled(Text)<{ descriptionColor?: string }>(({ theme, descriptionColor }) => ({
  fontSize: '16px',
  lineHeight: 1.6,
  fontWeight: 400,
  color: descriptionColor || '#8B8B8B',
  width: '100%',
  paddingLeft: '20px',
  paddingRight: '20px',
  whiteSpace: 'pre-line',
  [theme.mediaQueries.sm]: {
    fontSize: '17px',
  },
  [theme.mediaQueries.md]: {
    fontSize: '18px',
  },
}));
